import { FormControl, RadioGroup } from "@mui/material";
import Button from "@mui/material/Button";
import FormControlLabel from "@mui/material/FormControlLabel";
import MenuItem from "@mui/material/MenuItem";
import Radio from "@mui/material/Radio";
import { styled } from "@mui/styles";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import DiscussionSkeleton from "src/components/NewComponents/Skeletons/Discussion";
import { useReportQuestion } from "src/hooks/useExam";
import { useFontSize } from "src/hooks/useFontSize";
import ErrorIcon from "src/images/Icons/newIcons/exam/more/error.svg";

import { Select } from "../../../Default/LekUI/Select";
import { TextArea } from "../../../Default/LekUI/TextArea";
import DefaultOptionDetail from "./DefaultOptionDetail";

const reasons = [
	"questionInconsistentWithCurrentKnowledge",
	"wrongAnswer",
	"wrongCategory",
	"missingImages",
	"bugInQuestion",
	"another",
];

function ReportErrorInQuestion(props) {
	const { fontSizeClass } = useFontSize();
	const { t } = useTranslation();
	const correctAnswerOptions = [
		"A",
		"B",
		"C",
		"D",
		...(import.meta.env.SERVICE === "sistesty" ? [] : ["E"]),
		t("exam.reportError.modal.withoutCorrectAnswer"),
	];
	const { mutateAsync } = useReportQuestion();
	const initialValue = {
		value: reasons[0],
		sourceInfo: "",
		stipulation: "",
		correctAnswer: "",
	};
	const [details, setDetails] = useState(initialValue);

	useEffect(() => {
		setDetails({
			value: reasons[0],
			sourceInfo: "",
			stipulation: "",
			correctAnswer: "",
		});
	}, []);

	const setDetail = (key, value) => {
		setDetails({
			...details,
			[key]: value,
		});
	};

	const renderCorrectAnswer = () => {
		if (details.value !== reasons[1]) {
			return null;
		}

		return (
			<FormControl variant="outlined" size="small" fullWidth>
				<Select
					value={details.correctAnswer}
					onChange={(e) => setDetail("correctAnswer", e.target.value)}
					placeholder={t("exam.reportError.modal.inputs.correctAnswer")}
				>
					{correctAnswerOptions.map((option) => {
						return (
							<MenuItem value={option} key={option}>
								{option}
							</MenuItem>
						);
					})}
				</Select>
			</FormControl>
		);
	};

	const renderTextAreas = () => {
		return (
			<TextArea
				id="outlined-helperText"
				variant="outlined"
				fullWidth
				placeholder={t("exam.reportError.modal.inputs.stipulation")}
				value={details.stipulation}
				multiline
				rows={4}
				onChange={(e) => setDetail("stipulation", e.target.value)}
			/>
		);
	};

	const renderSources = () => {
		if ([reasons[2]].includes(details.value)) {
			return null;
		}

		return (
			<TextArea
				id="outlined-helperText"
				variant="outlined"
				fullWidth
				placeholder={t("exam.reportError.modal.inputs.sourceOfInformation")}
				value={details.sourceInfo}
				multiline
				rows={4}
				onChange={(e) => setDetail("sourceInfo", e.target.value)}
			/>
		);
	};

	const renderOption = () => {
		return reasons.map((reason) => (
			<FormControlLabel
				key={reason}
				value={reason}
				control={<Radio />}
				label={t(`exam.reportError.modal.${reason}`)}
			/>
		));
	};

	const report = async () => {
		await mutateAsync({ id: props.question.id, details });
		toast.success(t("exam.reportError.success"), {
			position: toast.POSITION.BOTTOM_LEFT,
			pauseOnHover: false,
			autoClose: 3000,
		});
		setDetails(initialValue);
	};

	const renderContent = () => {
		return (
			<div className="flex flex-col gap-20">
				<FormControl>
					<RadioGroup
						defaultValue={reasons[0]}
						onChange={(_e, value) => setDetail("value", value)}
					>
						{renderOption()}
					</RadioGroup>
				</FormControl>
				<div className="flex flex-col gap-8">
					{renderCorrectAnswer()}
					{renderTextAreas()}
					{renderSources()}
				</div>
				<div className="flex align-self-end gap-24">
					<Button onClick={props.onClose}>{t("commands.cancel")}</Button>
					<Button variant="contained" onClick={report} data-test-id="report-error-send-button">
						{t("commands.send")}
					</Button>
				</div>
			</div>
		);
	};

	return (
		props.settings.reportError && (
			<DefaultOptionDetail
				dispatch={props.dispatch}
				actions={props.actions}
				renderContent={renderContent}
				isOpen={props.settings.reportError}
				actionKey="reportError"
				renderHeader={() => (
					<div className="flex-center-row gap-8">
						<ErrorIcon className="icon-normal" />
						<span className={fontSizeClass}>{t("exam.reportError.title")}</span>
					</div>
				)}
				Skeleton={<DiscussionSkeleton />}
			/>
		)
	);
}

export default styled(ReportErrorInQuestion)``;
